import { useCallback, useEffect, useState } from "react";
import { useDashboardEditorContext } from "../../../../../../hook/context/editor";
import { useGUIEditorContext } from "../../../../../../hook/context/gui";
import useDebouncedCallback from "@ui/lib/hooks/useDebouncedCallback";
import type { EncodingT } from "../../../../../../schema/encoding";
import { getAvailableResourceKind } from "../../../../../../utils/getAvailableResourceKind";
import { useAvailableResources } from "../../../../../../hook/useAvailableResources";

export const useLayerUpdateColorEncoding = ({ projectId }: { projectId: string }) => {
  const editor = useDashboardEditorContext();
  const { block, preset } = useGUIEditorContext();
  const [layerEncodingColor, setLayerEncodingColor] = useState<string>();
  const availableResources = useAvailableResources({ projectId });

  useEffect(() => {
    const color = block?.config.layers?.[0]?.encoding.color;
    if (!color) return;
    const value =
      preset === "line" ||
      color.column === "group" ||
      color.column === "date" ||
      color.column === "hour" ||
      color.column === "dayOfYear"
        ? color.column
        : `${color.column}-${color.identifier}`;
    setLayerEncodingColor(value);
  }, [block, preset]);

  const updateBlockLayerEncodingColor = useDebouncedCallback(
    (value: string) => {
      const [column, identifier] = value.split("-");

      if (!block || (value.length > 0 && !column)) return;

      void editor?.updateBlock({
        ...block,
        data: {
          ...block.data,
        },
        config: {
          ...block.config,
          layers: block.config.layers?.map(layer => {
            if (!column)
              return {
                ...layer,
                encoding: {
                  ...layer.encoding,
                  color: undefined,
                },
              };

            let color;

            if (
              preset === "scatter" &&
              (layer.encoding.x.identifier === identifier ||
                layer.encoding.y.identifier === identifier ||
                ["group", "date", "hour", "dayOfYear"].includes(column))
            ) {
              color = {
                column,
                identifier:
                  column === "group" ||
                  column === "date" ||
                  column === "hour" ||
                  column === "dayOfYear"
                    ? layer.encoding.x.identifier
                    : identifier,
              };
            }

            // line has no identifier
            if (preset === "line") {
              const identifier = layer.encoding.y.identifier;
              const kind = getAvailableResourceKind(availableResources, identifier);
              if (
                (kind === "FAULT" && column === "faultStatus") ||
                (kind === "QUANTITY" && column === "validity")
              ) {
                color = { column, identifier: layer.encoding.y.identifier };
              }
            }

            return {
              ...layer,
              encoding: {
                ...layer.encoding,
                color: value.length > 0 && column ? (color as EncodingT) : undefined,
              },
            };
          }),
        },
      });
    },
    [availableResources, block, editor, preset],
    500,
  );

  const updateLayerEncodingColor = useCallback(
    (value: string) => {
      setLayerEncodingColor(value);
      updateBlockLayerEncodingColor(value);
    },
    [updateBlockLayerEncodingColor],
  );

  return { layerEncodingColor, updateLayerEncodingColor };
};
